import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import GlassCard from '../ui/GlassCard';
import GlassButton from '../ui/GlassButton';
import { ctaLinks, bookCallUrl } from '../../constants/content';
import { track } from '../../lib/analytics';
import { useSectionView } from '../../hooks/useSectionView';

export default function CTASection() {
  const { t } = useTranslation();
  const { darkMode } = useTheme();
  const sectionRef = useSectionView('cta', 8);
  const handleBookCall = () => {
    track('cta_clicked', {
      cta_id: 'cta_book_call',
      cta_text: t('cta.submitText'),
      section: 'cta',
      destination_url: bookCallUrl,
      destination_type: 'calendar'
    });
  };

  return (
    <section
      ref={sectionRef}
      id="contact"
      className={`py-24 relative overflow-hidden ${
        darkMode ? 'bg-main-black' : 'bg-gradient-to-b from-gray-50 to-white'
      }`}
    >
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full blur-3xl opacity-10 ${
          darkMode ? 'bg-main' : 'bg-main/50'
        }`} />
      </div>

      <div className="mainCont relative z-10">
        <div className="max-w-2xl mx-auto">
          <GlassCard padding="xl">
            {/* Header */}
            <h2 className={`titleFont text-3xl md:text-4xl font-bold text-center mb-4 ${
              darkMode ? 'text-white' : 'text-gray-900'
            }`}>
              {t('cta.headline')}
            </h2>

            <p className={`text-center mb-8 ${
              darkMode ? 'text-gray-400' : 'text-gray-600'
            }`}>
              {t('cta.subheadline')}
            </p>

            {/* Booking goes to the founders' shared calendar (paltalabs.io/cal). The n8n contact form
                it replaced posted to a webhook that no longer answers. */}
            <div className="flex justify-center">
              <GlassButton
                variant="primary"
                size="lg"
                href={bookCallUrl}
                onClick={handleBookCall}
              >
                {t('cta.submitText')}
                <svg
                  className="w-5 h-5 ml-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 8l4 4m0 0l-4 4m4-4H3"
                  />
                </svg>
              </GlassButton>
            </div>

            {/* Alternative links */}
            <div className={`mt-8 pt-6 border-t text-center ${
              darkMode ? 'border-white/10' : 'border-gray-200'
            }`}>
              <p className={`text-sm mb-3 ${
                darkMode ? 'text-gray-400' : 'text-gray-600'
              }`}>
                {t('cta.alternativeText')}
              </p>
              <div className="flex items-center justify-center gap-4">
                {ctaLinks.map((link) => (
                  <a
                    key={link.id}
                    href={link.href}
                    onClick={() => {
                      track('cta_clicked', {
                        cta_id: `cta_${link.id}`,
                        cta_text: t(`cta.links.${link.id}`),
                        section: 'cta',
                        destination_url: link.href,
                        destination_type: link.id === 'apiKey' ? 'app' : 'discord'
                      });
                    }}
                    className="text-main hover:text-main2 font-medium transition-colors"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t(`cta.links.${link.id}`)}
                  </a>
                ))}
              </div>
            </div>

            {/* Trust text */}
            <p className={`mt-6 text-center text-sm ${
              darkMode ? 'text-gray-500' : 'text-gray-500'
            }`}>
              {t('cta.trustText')}
            </p>
          </GlassCard>
        </div>
      </div>
    </section>
  );
}
